import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCockpitApi } from '@/api/provider';
import type { CustomerAccountRole } from '@/api/cockpit';

const DEFAULT_STALE = 30_000;

export function useExceptions() {
  const api = useCockpitApi();
  return useQuery({
    queryKey: ['cockpit', 'exceptions'],
    queryFn: () => api.exceptions(),
    staleTime: DEFAULT_STALE,
  });
}

export function useQueue(opts?: { includeResolved?: boolean }) {
  const api = useCockpitApi();
  const includeResolved = opts?.includeResolved ?? false;
  return useQuery({
    queryKey: ['cockpit', 'queue', { includeResolved }],
    queryFn: () => api.queue({ includeResolved }),
    staleTime: DEFAULT_STALE,
  });
}

export function useQueueViewConfig() {
  const api = useCockpitApi();
  return useQuery({
    queryKey: ['cockpit', 'queue-view-config'],
    queryFn: () => api.queueViewConfig(),
    staleTime: DEFAULT_STALE,
  });
}

export function useException(id: string | undefined) {
  const api = useCockpitApi();
  return useQuery({
    queryKey: ['cockpit', 'exception', id],
    enabled: Boolean(id),
    queryFn: async () => {
      if (!id) throw new Error('missing id');
      return api.exception(id);
    },
  });
}

export function useExceptionSubmit() {
  const api = useCockpitApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: string;
      fields: Record<string, string>;
      feedback: string;
      action: 'resume' | 'close';
    }) =>
      api.submitExceptionCorrection(input.id, {
        fields: input.fields,
        feedback: input.feedback,
        action: input.action,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cockpit', 'queue'] });
      void qc.invalidateQueries({ queryKey: ['cockpit', 'exceptions'] });
      void qc.invalidateQueries({ queryKey: ['cockpit', 'feedback'] });
    },
  });
}

export function useOperatorResolutionSave() {
  const api = useCockpitApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: string;
      addedLines?: readonly import('@/api/cockpit').ManualLineAddition[];
      fields: Record<string, string>;
      selectedProducts: Record<string, string>;
      selectedUnits: Record<string, string>;
      selectedQuantities: Record<string, string>;
      excludedLines: Record<string, { category: string; reason: string }>;
      fieldReasons: Record<string, string>;
      feedback?: string;
    }) =>
      api.saveOperatorResolution(input.id, {
        addedLines: input.addedLines ?? [],
        fields: input.fields,
        selectedProducts: input.selectedProducts,
        selectedUnits: input.selectedUnits,
        selectedQuantities: input.selectedQuantities,
        excludedLines: input.excludedLines,
        fieldReasons: input.fieldReasons,
        ...(input.feedback ? { feedback: input.feedback } : {}),
      }),
    onSuccess: (data, variables) => {
      qc.setQueryData(['cockpit', 'exception', variables.id], data);
      void qc.invalidateQueries({ queryKey: ['cockpit', 'queue'] });
      void qc.invalidateQueries({ queryKey: ['cockpit', 'exceptions'] });
      void qc.invalidateQueries({ queryKey: ['cockpit', 'feedback'] });
      void qc.invalidateQueries({ queryKey: ['cockpit', 'exception', variables.id] });
    },
  });
}

export function useStageRetry() {
  const api = useCockpitApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: string;
      mode: 'single' | 'from_here';
      stageKey:
        | 'ingestion'
        | 'ocr'
        | 'classification'
        | 'duplicate'
        | 'matching'
        | 'validation'
        | 'draft'
        | 'validating_in_d365'
        | 'confirm'
        | 'approval'
        | 'creating_sales_order'
        | 'acknowledgement'
        | 'resolved';
      substepId?: string | null;
    }) =>
      api.retryStage(input.id, {
        mode: input.mode,
        stageKey: input.stageKey,
        substepId: input.substepId ?? null,
      }),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: ['cockpit', 'queue'] });
      void qc.invalidateQueries({ queryKey: ['cockpit', 'exceptions'] });
      void qc.invalidateQueries({ queryKey: ['cockpit', 'exception', variables.id] });
    },
  });
}

export function useQueueDelete() {
  const api = useCockpitApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: string | readonly string[]) => {
      const ids = Array.isArray(input) ? input : [input];
      for (const id of ids) {
        await api.deleteQueueItem(id);
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cockpit', 'queue'] });
      void qc.invalidateQueries({ queryKey: ['cockpit', 'exceptions'] });
      void qc.invalidateQueries({ queryKey: ['cockpit', 'drafts'] });
      void qc.invalidateQueries({ queryKey: ['cockpit', 'feedback'] });
    },
  });
}

export function useProductSearch(id: string | undefined, query: string, enabled = true) {
  const api = useCockpitApi();
  const normalizedQuery = query.trim();
  return useQuery({
    queryKey: ['cockpit', 'product-search', id, normalizedQuery],
    enabled: enabled && Boolean(id) && normalizedQuery.length >= 2,
    staleTime: 30_000,
    queryFn: async () => {
      if (!id) throw new Error('missing id');
      return api.searchProducts(id, normalizedQuery);
    },
  });
}

export function useCustomerSearch(
  id: string | undefined,
  query: string,
  legalEntity?: string | null,
  role: CustomerAccountRole = 'ship_to',
  enabled = true,
) {
  const api = useCockpitApi();
  const normalizedQuery = query.trim();
  const normalizedLegalEntity = legalEntity?.trim().toLowerCase() ?? null;
  return useQuery({
    queryKey: ['cockpit', 'customer-search', id, normalizedLegalEntity, role, normalizedQuery],
    enabled: enabled && Boolean(id) && normalizedQuery.length >= 2,
    staleTime: 30_000,
    queryFn: async () => {
      if (!id) throw new Error('missing id');
      return api.searchCustomers(id, normalizedQuery, normalizedLegalEntity, role);
    },
  });
}

export function useDuplicateDecision() {
  const api = useCockpitApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; decision: 'duplicate' | 'not_duplicate' }) =>
      api.applyDuplicateDecision(input.id, { decision: input.decision }),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: ['cockpit', 'queue'] });
      void qc.invalidateQueries({ queryKey: ['cockpit', 'exceptions'] });
      void qc.invalidateQueries({ queryKey: ['cockpit', 'exception', variables.id] });
    },
  });
}
